import React from 'react';
import { Check } from 'lucide-react';

export default function TrainingGoals({ lang }) {
  const t = {
    sectionTag: lang === 'bg' ? 'ТВОЯТ НАПРЕДЪК' : 'YOUR PROGRESS',
    headline: lang === 'bg'
      ? 'ТРЕНИРАЙ КАТО СПОРТИСТ — ПОСТИГНИ ШАМПИОНСКИ РЕЗУЛТАТИ'
      : 'TRAIN LIKE AN ATHLETE — ACHIEVE CHAMPION RESULTS',
    sub: lang === 'bg'
      ? 'Тренировки, адаптирани към твоите фитнес цели'
      : 'Training tailored to your fitness goals',
    items: lang === 'bg'
      ? [
          'Протоколи за скорост, ловкост и взривна сила',
          'Специфични тренировки за футбол, баскетбол, бягане и повече',
          'Проследяване на VO2 max, скорост и спортно представяне',
          'Изградено от треньори и бивши професионалисти',
        ]
      : [
          'Speed, agility and explosive power protocols',
          'Sport-specific training for football, basketball, running and more',
          'Track VO2 max, speed and athletic performance',
          'Built by coaches and former professionals',
        ],
  };

  return (
    <section className="relative bg-[#1e293b] py-20 md:py-28 overflow-hidden">
      {/* Top angled */}
      <div
        className="absolute top-0 left-0 right-0 h-20 bg-[#0f172a]"
        style={{ clipPath: 'polygon(0 0, 100% 0, 100% 100%)' }}
      />

      {/* Decorative orange glow */}
      <div className="absolute -left-40 top-1/2 -translate-y-1/2 w-96 h-96 rounded-full bg-orange-500/10 blur-3xl pointer-events-none" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-2 gap-12 md:gap-20 items-center">
          {/* Left: text */}
          <div>
            <div className="inline-flex items-center gap-2 mb-4">
              <span className="w-6 h-[2px] bg-orange-500" />
              <span className="text-orange-400 font-bold text-xs tracking-[0.3em] uppercase">
                {t.sectionTag}
              </span>
            </div>

            <h2 className="font-black uppercase text-3xl sm:text-4xl md:text-5xl leading-tight mb-4 text-white">
              {t.headline}
            </h2>

            <p className="text-slate-400 text-base md:text-lg mb-10 font-medium">
              {t.sub}
            </p>

            <ul className="space-y-5">
              {t.items.map((item, i) => (
                <li key={i} className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-orange-500 flex items-center justify-center shadow-lg shadow-orange-500/30 mt-0.5">
                    <Check className="w-4 h-4 text-white" strokeWidth={3} />
                  </div>
                  <span className="text-slate-200 text-base md:text-lg leading-relaxed">
                    {item}
                  </span>
                </li>
              ))}
            </ul>

            <div className="mt-10">
              <a
                href="/s"
                className="inline-flex items-center gap-2 px-8 py-4 rounded-full font-black tracking-widest uppercase text-white transition-all duration-300 hover:scale-105 shadow-xl shadow-orange-500/30 hover:shadow-orange-500/50"
                style={{
                  background: 'linear-gradient(135deg, #f97316 0%, #f59e0b 100%)',
                }}
              >
                {lang === 'bg' ? 'СТАНИ ПРО' : 'GO PRO'}
              </a>
            </div>
          </div>

          {/* Right: visual stats */}
          <div className="relative">
            <div className="grid grid-cols-2 gap-4">
              {[
                { num: '500+', label: lang === 'bg' ? 'Протоколи' : 'Protocols' },
                { num: '50K+', label: lang === 'bg' ? 'Атлети' : 'Athletes' },
                { num: '4.9★', label: lang === 'bg' ? 'Рейтинг' : 'Rating' },
                { num: '15+', label: lang === 'bg' ? 'Вида спорт' : 'Sports' },
              ].map((stat, i) => (
                <div
                  key={i}
                  className="relative bg-[#0f172a] rounded-2xl p-6 border border-slate-700/50 hover:border-orange-500/50 transition-all duration-300 group overflow-hidden"
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-orange-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                  <div
                    className="text-3xl md:text-4xl font-black mb-1"
                    style={{
                      background: 'linear-gradient(135deg, #f97316, #f59e0b)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text',
                    }}
                  >
                    {stat.num}
                  </div>
                  <div className="text-slate-400 text-sm font-semibold uppercase tracking-wider">
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>

            {/* Decorative ring */}
            <div className="absolute -bottom-8 -right-8 w-48 h-48 rounded-full border-2 border-orange-500/20 pointer-events-none" />
            <div className="absolute -bottom-4 -right-4 w-32 h-32 rounded-full border-2 border-amber-400/20 pointer-events-none" />
          </div>
        </div>
      </div>

      {/* Bottom angled */}
      <div
        className="absolute bottom-0 left-0 right-0 h-20 bg-[#0f172a]"
        style={{ clipPath: 'polygon(0 100%, 100% 0, 100% 100%)' }}
      />
    </section>
  );
}
